import React, {useCallback} from 'react';
import {Box, Tab, Tabs} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import FolderIcon from '@mui/icons-material/Folder';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import ListAltIcon from '@mui/icons-material/ListAlt';
import HistoryIcon from '@mui/icons-material/History';
import {Note, SvgIconComponent} from '@mui/icons-material';
import {capitalize} from 'lodash-es';
import {Link, useNavigate, useParams} from 'react-router-dom';
import * as Sentry from '@sentry/react';

import TabPanel, {tabPanelAriaProps} from '../../components/TabPanel/TabPanel';
import ACCOUNT_TAB from '../../constants/AccountTab';
import {
  AccountNavigationParams,
  useAccountContext,
  useAccountFull,
} from '../../contexts/AccountContext';
import ClientGoalsPage from '../ClientGoalsPage/ClientGoalsPage';
import useTitle from '../../hooks/useTitle';
import AccountOverview from '../../components/AccountOverview/AccountOverview';
import ReviewsPanel from '../../components/Reviews/ReviewsPanel';
import ClientAnalyticsPage from '../ClientAnalyticsPage/ClientAnalyticsPage';
import {InstructionsPage} from '../AccountInstructionsPage/AccountInstructionsPage';
import {BusinessUnitEnum, GlobalPermissionEnum} from '../../schema-types/globalTypes';
import {ErrorPage} from '../ErrorPage';
import {VulnerableClientBanner} from '../../components/VulnerableClientBanner/VulnerableClientBanner';
import {KeyClientDataBanner} from '../../components/KeyClientDataBanner/KeyClientDataBanner';
import {useClientDataValidation} from '../../hooks/useClientDataValidation';
import {ClientDocumentsPage} from '../ClientDocumentsPageV1/ClientDocumentsPage';
import {FeatureBoundary} from '../../contexts/FeatureBoundary';
import {Button} from '../../components/Button/Button';
import {AccountBasicFragment} from '../../schema-types/AccountBasicFragment';
import {AccountNotes} from '../../components/AccountOverview/AccountNotes';
import {useDebugContext} from '../../contexts/DebugProvider';
import {AccountHistoryPage} from '../../components/AccountHistory/AccountHistory';
import {useDjangoContext} from '../../contexts/useAuthContext';
import {AddAccountDummyDataButton} from '../../components/AccountOverview/AddAccountDummyDataButton';

export const ALL_BUSINESS_UNITS: BusinessUnitEnum[] = Object.values(BusinessUnitEnum);

export type TabConfigType = {
  key: ACCOUNT_TAB;
  label: string;
  Icon: SvgIconComponent;
  Component: React.FC;
  visibleTo: BusinessUnitEnum[];
};

const tabConfig: TabConfigType[] = [
  {
    key: ACCOUNT_TAB.OVERVIEW,
    label: capitalize(ACCOUNT_TAB.OVERVIEW),
    Icon: SettingsIcon,
    Component: AccountOverview,
    visibleTo: ALL_BUSINESS_UNITS,
  },
  {
    key: ACCOUNT_TAB.REVIEWS,
    label: capitalize(ACCOUNT_TAB.REVIEWS),
    Icon: FindInPageIcon,
    Component: ReviewsPanel,
    visibleTo: [BusinessUnitEnum.AR, BusinessUnitEnum.SFP],
  },
  {
    key: ACCOUNT_TAB.GOALS,
    label: capitalize(ACCOUNT_TAB.GOALS),
    Icon: TrendingUpIcon,
    Component: ClientGoalsPage,
    visibleTo: [BusinessUnitEnum.AR, BusinessUnitEnum.SFP],
  },
  {
    key: ACCOUNT_TAB.ANALYTICS,
    label: capitalize(ACCOUNT_TAB.ANALYTICS),
    Icon: EqualizerIcon,
    Component: ClientAnalyticsPage,
    visibleTo: ALL_BUSINESS_UNITS,
  },
  {
    key: ACCOUNT_TAB.DOCUMENTS,
    label: capitalize(ACCOUNT_TAB.DOCUMENTS),
    Icon: FolderIcon,
    Component: ClientDocumentsPage,
    visibleTo: ALL_BUSINESS_UNITS,
  },
  {
    key: ACCOUNT_TAB.INSTRUCTIONS,
    label: 'Instructions',
    Icon: ListAltIcon,
    Component: InstructionsPage,
    visibleTo: [BusinessUnitEnum.DA],
  },
  {
    key: ACCOUNT_TAB.NOTES,
    label: capitalize(ACCOUNT_TAB.NOTES),
    Icon: Note,
    Component: AccountNotes,
    visibleTo: [BusinessUnitEnum.AR, BusinessUnitEnum.SFP],
  },
  {
    key: ACCOUNT_TAB.HISTORY,
    label: capitalize(ACCOUNT_TAB.HISTORY),
    Icon: HistoryIcon,
    Component: AccountHistoryPage,
    visibleTo: ALL_BUSINESS_UNITS,
  },
];

export const makeVisibleToFilter =
  (businessUnit: BusinessUnitEnum, perms: GlobalPermissionEnum[]) =>
  (tab: TabConfigType) => {
    /**
     * Add to this “Audit History” for all accounts, but wrapped in a flag.
     * Flag is only enabled for admin groups.
     */
    if (
      tab.key === ACCOUNT_TAB.HISTORY &&
      !perms.includes(GlobalPermissionEnum.can_access_admin_features)
    ) {
      return false;
    }
    return !!businessUnit && tab.visibleTo.includes(businessUnit);
  };

function AccountDetails() {
  const navigate = useNavigate();
  const {perms} = useDjangoContext();
  const {debug, setDebug} = useDebugContext();
  const {account, accountId, accountType} = useAccountContext();
  const {accountPageTabKey = ACCOUNT_TAB.OVERVIEW} = useParams<AccountNavigationParams>();
  useTitle(`${capitalize(accountPageTabKey)} - ${account?.name}`);
  const isSFP = accountType === BusinessUnitEnum.SFP;

  const {clientDataIssues} = useClientDataValidation();

  const setAccountPageTabKey = useCallback(
    (index: string) => {
      navigate({pathname: `/account/${accountId}/${index}`});
    },
    [accountId, navigate]
  );

  if (!accountType) {
    return (
      <ErrorPage text="This account does not have a business unit assigned to it. Please assign a business unit to view this page, or go to Saltus support for assistance in doing this." />
    );
  }

  return (
    <>
      <KeyClientDataBanner issues={clientDataIssues} isSFP={isSFP} />
      <VulnerableClientBanner contacts={account?.contacts} />
      <Box position="relative">
        {debug.forms && <AccountDebug account={account} />}
        <Tabs
          sx={{
            marginBottom: 2,
          }}
          centered
          value={accountPageTabKey}
          onChange={(_, index) => {
            Sentry.addBreadcrumb({
              message: `Open ${index}`,
              category: 'navigation',
              level: 'info',
            });
            setAccountPageTabKey(index);
          }}
          aria-label="Account section tabs"
        >
          {tabConfig
            .filter(makeVisibleToFilter(accountType, perms))
            .map(({key, label, Icon}) => (
              <Tab
                sx={{marginLeft: 2, marginRight: 2}}
                key={key}
                icon={<Icon fontSize="small" />}
                label={label}
                {...tabPanelAriaProps(key)}
                value={key}
              />
            ))}
        </Tabs>
        {tabConfig
          .filter(makeVisibleToFilter(accountType, perms))
          .map(({key, Component}) => (
            <TabPanel key={key} index={accountPageTabKey} value={key}>
              <Component />
            </TabPanel>
          ))}
      </Box>
    </>
  );
}

const AccountDebug: React.FC<{account?: AccountBasicFragment}> = ({account}) => {
  const {accountFull} = useAccountFull();
  return (
    <FeatureBoundary
      showErrorIfDenied={false}
      features={[GlobalPermissionEnum.can_restore_admin_permission]}
    >
      <Box
        position="absolute"
        display="flex"
        flexDirection="column"
        gap={0.5}
        top={-5}
        right={0}
        zIndex={100}
      >
        <Link
          to={{
            pathname: `/test/test-any-open-review/${account?.curoId}`,
            search: 'goTo=recommends-builder',
          }}
        >
          <Button size="small" variant="contained" eventName="Test create recommendation">
            Create recc
          </Button>
        </Link>
        <FeatureBoundary
          features={[GlobalPermissionEnum.can_access_dentist_recommendation_builder]}
          showErrorIfDenied={false}
        >
          <Link
            to={{
              pathname: `/test/test-any-open-review/${account?.curoId}`,
              search: 'goTo=builder',
            }}
          >
            <Button size="small" variant="contained" eventName="Test create builder">
              Create builder
            </Button>
          </Link>
        </FeatureBoundary>
        <Link
          to={{
            pathname: `/test/test-any-open-review/${account?.curoId}`,
          }}
        >
          <Button size="small" variant="contained" eventName="Test open any review">
            Any open review
          </Button>
        </Link>
        <Link
          to={{
            pathname: `/test/test-new-review/${account?.curoId}`,
            search: 'goTo=header-precursor',
          }}
        >
          <Button size="small" variant="contained" eventName="Test force create review">
            Create adhoc
          </Button>
        </Link>
        <AddAccountDummyDataButton account={accountFull} />
      </Box>
    </FeatureBoundary>
  );
};

export default AccountDetails;
